import React, { useContext, useEffect } from 'react';
import { connect, ReactReduxContext } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import NewProductReleases from '../../components/new-product-releases';
import CampaignDeals from '../../components/campaign-deals';
import CampaignBanner from './components/campaign-banner';
import ForumTopicListing from './components/forum-topic-listing';
import PageContent from '../../components/page-content';
import FeaturedArticle from './components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { CategoryList } from '../../components/category-list';
import ProductRecommendations from './components/product-recommendations';
import AdContainer from '../../components/ads';
import SotyBanner from './components/soty-banner';
import ExpertSection from './components/experts';
import ErrorBoundary from '../../components/error-boundary';
import { plainText } from '../editorial-content/components/slices/rich-text';
import { Column } from '@prisjakt/ui/list/variants';

import {
  LandingPageQuery,
  Campaign,
  CampaignBanner as CampaignBannerType,
  ExpertLandingPage,
  Maybe,
} from '../../types';
import { AppState } from '../../reducers';

import * as S from './styles';
import PopularFacets from './components/popular-facets';
import { CategoryWithLogo } from 'src/components/category-list/types';

type OwnProps = {
  data: LandingPageQuery & {
    expertLandingPage: ExpertLandingPage;
  };
};

type StateProps = {
  activeCampaign?: Campaign;
  bigCampaignBanner?: CampaignBannerType;
  campaignBanners?: AppState['globals']['campaignBanners'];
};

type Props = OwnProps & StateProps;

const LandingPage = ({
  bigCampaignBanner,
  activeCampaign,
  campaignBanners,
  data,
}: Props) => {
  const { t, market } = useContext(AppContext);
  const { store } = useContext(ReactReduxContext);

  const {
    campaignDeals,
    forumTopics,
    personalizedRecommendations,
    fallbackRecommendations,
    newProductRecommendations,
    featuredArticle,
    rootCategories,
    popularFacets,
  } = data;

  useEffect(() => {
    store.dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: 'landing-page:mounted',
    });
  }, [store]);

  const categories: Maybe<CategoryWithLogo>[] = (rootCategories || [])
    .map(category =>
      category
        ? ({
            ...category,
            logo: { 192: category.logo },
          } as CategoryWithLogo)
        : null
    )
    .filter(x => x);

  const activeCampaignLogo =
    activeCampaign &&
    activeCampaign.banners &&
    activeCampaign.banners.find(
      banner => banner && banner.type.toUpperCase() === 'BANNER_LOGO_BIG'
    );

  const activeCampaignAsCategory: CategoryWithLogo | undefined =
    activeCampaign &&
    ({
      id: 'activeCampaign',
      name: activeCampaign.name,
      pathName: activeCampaign.web_uri && activeCampaign.web_uri.responsive,
      logo: {
        192: activeCampaignLogo ? activeCampaignLogo.imageUrl : null,
      },
    } as CategoryWithLogo);

  const beforeBanners =
    campaignBanners &&
    campaignBanners.active.filter(
      (b: CampaignBannerType) => b && b.type === 'BEFORE_PROMO_BANNER'
    );

  const categoriesWithActiveCampaign = activeCampaignAsCategory
    ? [activeCampaignAsCategory, ...categories]
    : categories;

  return (
    <S.LandingPage>
      <PageContent withAds headerHeight={0} bottomPlacements={[]}>
        <S.Content>
          <AdContainer
            onlyWithServices={['publisherdesk']}
            placement="mob-banner-ad-a"
          />
          <Panel
            header={<PanelHeader title={t('landing', 'categories.title')} />}
            data-test-type="categories"
          >
            <CategoryList
              withAds
              displayLinkedCategories={false}
              categories={categoriesWithActiveCampaign}
            />
          </Panel>

          <AdContainer
            onlyWithServices={['publisherdesk']}
            placement="mob-banner-ad-b"
          />

          {bigCampaignBanner && (
            <CampaignBanner
              url={bigCampaignBanner.url}
              imageUrl={bigCampaignBanner.imageUrl}
              {...bigCampaignBanner.options}
            />
          )}

          {beforeBanners && beforeBanners.length > 0 && (
            <PromoBanner inverted banners={beforeBanners} />
          )}

          {market.shopOfTheYear?.isVotingOpen && (
            <SotyBanner year={market.shopOfTheYear.year} />
          )}

          {market.experts.enabled && data.expertLandingPage && (
            <Panel header={<PanelHeader />}>
              <ExpertSection data={data.expertLandingPage} />
            </Panel>
          )}

          {data.prismicArticles &&
            data.prismicArticles.nodes.length > 0 &&
            data.prismicArticles.nodes.map((article, index) => {
              const ArticleComponent = (
                <Panel key={article.id} header={<PanelHeader />}>
                  <FeaturedArticle
                    pathName={article.pathName}
                    imageUrl={
                      article.data.featuredImage &&
                      article.data.featuredImage.url
                    }
                    title={plainText(article.data.title)}
                    preamble={plainText(article.data.metaDescription)}
                    linkText={t('landing', 'featured-article.read-more')}
                  />
                </Panel>
              );

              if (index === 0) {
                return (
                  <React.Fragment key="ad-container">
                    {ArticleComponent}
                    <AdContainer
                      placement="dubbelpanorama2"
                      format="panorama_2"
                      min={0}
                      max={860}
                      spacing={{ bottom: '20px' }}
                      triggerPoint={100}
                    />
                  </React.Fragment>
                );
              }

              return ArticleComponent;
            })}

          {featuredArticle && (
            <Panel header={<PanelHeader />}>
              <FeaturedArticle
                {...featuredArticle}
                linkText={t('landing', 'featured-article.read-more')}
              />
            </Panel>
          )}

          <Column gap={1.5}>
            <RecentlyVisitedProducts />

            {fallbackRecommendations && fallbackRecommendations.products && (
              <ErrorBoundary onError={() => null}>
                <ProductRecommendations {...fallbackRecommendations} />
              </ErrorBoundary>
            )}

            <CampaignDeals deals={campaignDeals} />

            {personalizedRecommendations &&
              personalizedRecommendations.products &&
              !personalizedRecommendations.products.fallbackToDefault && (
                <ErrorBoundary onError={() => null}>
                  <ProductRecommendations {...personalizedRecommendations} />
                </ErrorBoundary>
              )}

            {newProductRecommendations && (
              <NewProductReleases {...newProductRecommendations} />
            )}

            <AdContainer
              placement="dubbelpanorama3"
              format="panorama_3"
              min={0}
              max={860}
              spacing={{ bottom: '20px' }}
              triggerPoint={100}
            />

            {forumTopics && <ForumTopicListing topics={forumTopics} />}

            {popularFacets && <PopularFacets popularFacets={popularFacets} />}
          </Column>
        </S.Content>
      </PageContent>
    </S.LandingPage>
  );
};

const mapStateToProps = (state: AppState) => ({
  activeCampaign:
    state.globals.campaigns &&
    state.globals.campaigns.active.find(
      (c: Campaign) => c.type === 'OFFER' || c.type === 'SEARCH_BANNER'
    ),
  bigCampaignBanner:
    state.globals.campaignBanners &&
    state.globals.campaignBanners.active.find(
      (banner: CampaignBannerType) => banner.type === 'LANDING_PAGE_BIG'
    ),
  campaignBanners: state.globals.campaignBanners,
});

const dispatchActions = {};

export default connect(mapStateToProps, dispatchActions)(LandingPage);
